import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import { format, differenceInDays } from 'date-fns';
import { FiArchive, FiCalendar, FiCheckCircle, FiUsers, FiTool, FiLayers } from 'react-icons/fi';

const TaskHistory = () => {
  const [archivedTasks, setArchivedTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [archiving, setArchiving] = useState(false);
  const { user } = useAuth();
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await axios.get('/history');
      setArchivedTasks(response.data);
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveOld = async () => {
    if (!window.confirm('Archive all completed tasks older than 1 week? This will move them to history.')) {
      return;
    }

    setArchiving(true);
    try {
      const response = await axios.post('/history/archive-old');
      alert(response.data.message);
      // Refresh both tasks and history
      fetchHistory();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to archive tasks');
    } finally {
      setArchiving(false);
    }
  };

  const filteredTasks = archivedTasks.filter((task) => {
    if (filter === 'all') return true;
    if (filter === 'team') return task.isTeamTask;
    if (filter === 'individual') return !task.isTeamTask;
    return true;
  });

  const canArchive = user?.role === 'admin' || user?.role === 'productManager' || user?.role === 'teamLead';

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-300"></div>
        </div>
      </>
    );
  }

  return (
    <div className="min-h-screen bg-background relative">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white mb-2 flex items-center">
              <FiArchive className="mr-3" />
              History
            </h1>
            <p className="text-dark-100 text-sm">Archived tasks</p>
          </div>
          {canArchive && (
            <button
              onClick={handleArchiveOld}
              disabled={archiving}
              className="mt-4 sm:mt-0 flex items-center space-x-2 bg-purple-300 text-white px-6 py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FiArchive />
              <span>{archiving ? 'Archiving...' : 'Archive old'}</span>
            </button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-stroke border border-stroke rounded-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-dark-100 text-sm">Total Archived</span>
              <FiArchive className="text-purple-300" />
            </div>
            <p className="text-3xl font-bold text-white">{archivedTasks.length}</p>
          </div>
          <div className="bg-stroke border border-stroke rounded-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-dark-100 text-sm">Team Tasks</span>
              <FiUsers className="text-blue-400" />
            </div>
            <p className="text-3xl font-bold text-white">
              {archivedTasks.filter(t => t.isTeamTask).length}
            </p>
          </div>
          <div className="bg-stroke border border-stroke rounded-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-dark-100 text-sm">Individual Tasks</span>
              <FiCheckCircle className="text-green-400" />
            </div>
            <p className="text-3xl font-bold text-white">
              {archivedTasks.filter(t => !t.isTeamTask).length}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          {['all', 'team', 'individual'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === f
                  ? 'bg-purple-300 text-white'
                  : 'bg-stroke text-dark-100 hover:bg-dark-300'
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {/* Archived Tasks List */}
        {filteredTasks.length === 0 ? (
          <div className="bg-stroke border border-stroke rounded-lg p-12 text-center">
            <FiArchive className="text-6xl text-dark-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No archived tasks</h3>
            <p className="text-dark-100">
              Completed tasks older than 1 week will appear here
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredTasks.map((task) => {
              const daysSinceArchived = differenceInDays(new Date(), new Date(task.archivedAt));
              
              return (
                <div
                  key={task._id}
                  className="bg-stroke border border-stroke rounded-lg p-6 hover:border-purple-300 transition-colors"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-white mb-2">{task.title}</h3>
                      <div className="flex flex-wrap gap-2 mb-2">
                        <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs font-medium border border-green-500/50">
                          Completed
                        </span>
                        {task.isTeamTask && (
                          <span className="px-2 py-1 bg-purple-300/20 text-purple-200 rounded text-xs font-medium">
                            Team Task
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {task.description && (
                    <p className="text-dark-100 text-sm mb-4 line-clamp-2">{task.description}</p>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="flex items-center text-dark-100 text-sm">
                      <FiCalendar className="mr-2 text-white" />
                      <span>Completed: {format(new Date(task.completedAt), 'MMM dd, yyyy')}</span>
                    </div>
                    <div className="flex items-center text-dark-100 text-sm">
                      <FiArchive className="mr-2" />
                      <span>Archived {daysSinceArchived} day{daysSinceArchived !== 1 ? 's' : ''} ago</span>
                    </div>
                    <div className="flex items-center text-dark-100 text-sm">
                      <FiCheckCircle className="mr-2" />
                      <span>Created by: {task.createdBy?.name || 'Unknown'}</span>
                    </div>
                    {task.assignedTo && (
                      <div className="flex items-center text-dark-100 text-sm">
                        <FiUsers className="mr-2" />
                        <span>Assigned to: {task.assignedTo?.name || 'Team Member'}</span>
                      </div>
                    )}
                  </div>

                  {task.tools && task.tools.length > 0 && (
                    <div className="flex items-start text-dark-100 text-sm mb-2">
                      <FiTool className="mr-2 mt-1" />
                      <div className="flex flex-wrap gap-1">
                        {task.tools.slice(0, 5).map((tool, idx) => (
                          <span key={idx} className="bg-dark-300 px-2 py-1 rounded text-xs">
                            {tool}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {task.technologies && task.technologies.length > 0 && (
                    <div className="flex items-start text-dark-100 text-sm">
                      <FiLayers className="mr-2 mt-1" />
                      <div className="flex flex-wrap gap-1">
                        {task.technologies.slice(0, 5).map((tech, idx) => (
                          <span key={idx} className="bg-purple-300/20 text-purple-200 px-2 py-1 rounded text-xs">
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mt-4 pt-4 border-t border-dark-300">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-dark-100">
                        Deadline was: {format(new Date(task.deadline), 'MMM dd, yyyy')}
                      </span>
                      <span className="px-2 py-1 bg-purple-300/20 text-purple-200 rounded text-xs font-medium">
                        {task.priority} priority
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskHistory;
